/**
 * Medical reference data — dyshaemoglobinaemias.
 *
 * The classic "the pulse oximeter is lying" scenarios: SpO₂ does not reflect
 * true oxygenation and does NOT respond to FiO₂. Each carries cognitive-aid and
 * investigation cards (co-oximetry is the key test).
 *
 * Teaching values — condensed; defer to co-oximetry and local protocol.
 */
export const DYSHB_SCENARIOS = {
  methemoglobinemia: {
    name: 'Methemoglobinemia',
    description: 'SpO₂ stuck ~85% and unresponsive to O₂ ("saturation gap"); chocolate-brown blood. Cause: benzocaine/prilocaine, dapsone, nitrates.',
    vitals: { HR: 105, SpO2: 85, NIBP_Sys: 124, NIBP_Dia: 76, ABP_Sys: 122, ABP_Dia: 74, EtCO2: 36, RR: 22, Temp: 36.9, CVP: 5 },
    aid: [
      { title: 'Recognise', lines: [
        'SpO₂ plateaus around 85% regardless of FiO₂',
        'Cyanosis with a NORMAL PaO₂; chocolate-brown arterial blood',
        'Recent topical/local anaesthetic (benzocaine, prilocaine), dapsone, nitrates',
        'STOP the offending agent',
      ] },
      { title: 'Treatment', lines: [
        '100% O₂ (supports dissolved O₂ and tissue delivery)',
        'Methylene blue 1–2 mg/kg IV over 5 min',
        'May repeat once at 30–60 min if MetHb persists (max ~7 mg/kg)',
        'Send co-oximetry to confirm and trend MetHb',
      ] },
      { title: 'Cautions', lines: [
        'Methylene blue INEFFECTIVE and may cause haemolysis in G6PD deficiency',
        '→ use ascorbic acid, exchange transfusion, or hyperbaric O₂',
        'Serotonin syndrome risk with serotonergic drugs',
        'High doses of methylene blue can itself lower SpO₂ reading',
      ] },
    ],
    investigations: [
      { title: 'Co-oximetry', lines: [
        'MetHb 35% (normal < 1–2%)',
        'Pulse-ox SpO₂ 85% but ABG co-oximetry shows the "saturation gap"',
        'Pulse oximetry trends toward ~85% as MetHb rises (both wavelengths absorbed)',
      ] },
      { title: 'Physical Exam', lines: [
        'Central cyanosis UNRESPONSIVE to supplemental O₂',
        'Chocolate-brown arterial blood',
        'Dyspnoea, headache, fatigue; dysrhythmia/seizure if severe',
      ] },
      { title: 'ABG', lines: [
        'PaO₂ is NORMAL (the paradox — dissolved O₂ fine, Hb cannot carry it)',
        'Calculated SaO₂ from PaO₂ is high, but measured co-ox sat is low',
        'Metabolic acidosis / raised lactate if severe',
      ] },
    ],
  },

  carbonMonoxide: {
    name: 'Carbon Monoxide Poisoning',
    description: 'SpO₂ falsely NORMAL (~98%) despite tissue hypoxia — pulse ox cannot distinguish COHb from O₂Hb. Source: smoke, exhaust, dry CO₂ absorbent + volatile.',
    vitals: { HR: 112, SpO2: 98, NIBP_Sys: 118, NIBP_Dia: 72, ABP_Sys: 116, ABP_Dia: 70, EtCO2: 34, RR: 22, Temp: 37.0, CVP: 5 },
    aid: [
      { title: 'Recognise the Trap', lines: [
        'SpO₂ reads normal but the patient is hypoxic',
        'Headache, nausea, confusion, syncope; exposure to smoke/exhaust',
        'Anaesthesia clue: CO from volatile + desiccated CO₂ absorbent (first case Monday)',
        'Remove patient from the source',
      ] },
      { title: 'Treatment', lines: [
        'High-flow 100% O₂ via non-rebreather or ETT',
        'COHb half-life 320 min (air) → ~74 min on 100% O₂',
        'Continue until COHb < 5% AND symptoms resolve',
        'In fires: also consider cyanide toxicity → hydroxocobalamin',
      ] },
      { title: 'Hyperbaric O₂', lines: [
        'HBO shortens COHb half-life to ~20 min',
        'Consider for: loss of consciousness, neuro deficit, cardiac ischaemia,',
        'COHb > 25%, or pregnancy',
        'ECG + troponin (myocardial ischaemia); neuro assessment',
      ] },
    ],
    investigations: [
      { title: 'Co-oximetry', lines: [
        'COHb 28% (non-smoker < 3%, smoker < 10%)',
        'SpO₂ 98% is FALSELY reassuring — pulse ox counts COHb as O₂Hb',
        'A normal SpO₂ does NOT exclude CO poisoning — send co-oximetry',
      ] },
      { title: 'Physical Exam', lines: [
        'Headache, nausea, dizziness, confusion, syncope',
        '"Cherry-red" skin is classic but unreliable and late',
        'Neuro exam; assess for concurrent burns/inhalation injury',
      ] },
      { title: 'ABG', lines: [
        'PaO₂ NORMAL but arterial O₂ CONTENT low (CO occupies Hb + left-shifts curve)',
        'Metabolic (lactic) acidosis',
        'Calculated SaO₂ overestimates true O₂ saturation',
      ] },
    ],
  },
};
